import { cva } from 'class-variance-authority';
import * as React from 'react';

import { Button } from './button';
import { CONTROL_SIZE } from './types/control-enums';

import { cn } from '@/lib/utils';

import { INPUT_APPEARANCE } from './types/input-variants-enums';



type InputBaseProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> & {
  appearance?: INPUT_APPEARANCE;
  size?: CONTROL_SIZE;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
};

type LeftAdornmentProps =
  | {
      leftOnClick: () => void;
      leftAriaLabel: string;
    }
  | {
      leftOnClick?: undefined;
      leftAriaLabel?: string;
    };

type RightAdornmentProps =
  | {
      rightOnClick: () => void;
      rightAriaLabel: string;
      rightAriaPressed?: boolean;
    }
  | {
      rightOnClick?: undefined;
      rightAriaLabel?: string;
      rightAriaPressed?: boolean;
    };

export type InputProps = InputBaseProps & LeftAdornmentProps & RightAdornmentProps;

const ADORNMENT_BUTTON_CLASS =
  'absolute top-1/2 flex size-7 -translate-y-1/2 cursor-pointer items-center justify-center rounded-control border-none bg-transparent text-content-tertiary pointer-coarse:min-h-11 pointer-coarse:min-w-11';

const inputFoundationVariants = cva(
  'flex w-full rounded-control border border-border-control bg-surface-base text-content-primary transition-[color,background-color,border-color,box-shadow] duration-standard ease-standard file:border-0 file:bg-transparent file:font-normal file:text-inherit placeholder:text-content-tertiary pointer-fine:hover:border-border-strong focus-visible:border-focus-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring read-only:border-border-default read-only:bg-surface-inset read-only:text-content-secondary read-only:hover:border-border-default disabled:cursor-not-allowed disabled:border-border-subtle disabled:bg-surface-foundation-subtle disabled:text-content-disabled disabled:placeholder:text-content-disabled aria-invalid:border-status-danger-border aria-invalid:bg-surface-base aria-invalid:ring-2 aria-invalid:ring-error-ring aria-invalid:focus-visible:border-status-danger-border aria-invalid:focus-visible:ring-error-ring motion-reduce:transition-none pointer-coarse:h-11 pointer-coarse:text-base',
  {
    variants: {
      appearance: {
        [INPUT_APPEARANCE.DEFAULT]: '',
        [INPUT_APPEARANCE.MONO]: 'bg-surface-inset font-mono tabular-nums',
      },
      size: {
        [CONTROL_SIZE.COMPACT]: 'h-9 px-2.5 type-field-label',
        [CONTROL_SIZE.DEFAULT]: 'h-10 px-3 type-body',
        [CONTROL_SIZE.COMFORTABLE]: 'h-11 px-3.5 type-body',
      },
    },
  }
);

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      appearance = INPUT_APPEARANCE.DEFAULT,
      className,
      leftIcon,
      rightIcon,
      rightOnClick,
      leftOnClick,
      rightAriaLabel,
      rightAriaPressed,
      leftAriaLabel,
      size = CONTROL_SIZE.DEFAULT,
      type,
      disabled,
      ...props
    },
    ref
  ) => {
    const inputClassName = inputFoundationVariants({ appearance, size });

    if (leftIcon || rightIcon) {
      return (
        <div className="relative w-full">
          {leftIcon &&
            (leftOnClick ? (
              <Button
                type="button"
                variant="ghost"
                size="inline"
                aria-label={leftAriaLabel}
                disabled={disabled}
                className={cn(ADORNMENT_BUTTON_CLASS, 'left-1.5')}
                onClick={leftOnClick}
              >
                {leftIcon}
              </Button>
            ) : (
              <span
                className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-content-tertiary"
                aria-hidden="true"
              >
                {leftIcon}
              </span>
            ))}
          <input
            type={type}
            className={cn(
              inputClassName,
              leftIcon && 'pl-10',
              rightIcon && 'pr-10',
              className
            )}
            ref={ref}
            disabled={disabled}
            {...props}
          />
          {rightIcon &&
            (rightOnClick ? (
              <Button
                type="button"
                variant="ghost"
                size="inline"
                aria-label={rightAriaLabel}
                aria-pressed={rightAriaPressed}
                disabled={disabled}
                className={cn(ADORNMENT_BUTTON_CLASS, 'right-1.5')}
                onClick={rightOnClick}
              >
                {rightIcon}
              </Button>
            ) : (
              <span
                className={cn(
                  'pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-content-tertiary'
                )}
                aria-hidden="true"
              >
                {rightIcon}
              </span>
            ))}
        </div>
      );
    }
    return (
      <input
        type={type}
        className={cn(
          inputClassName,
          leftIcon && 'pl-10',
          rightIcon && 'pr-10',
          className
        )}
        ref={ref}
        disabled={disabled}
        {...props}
      />
    );
  }
);

Input.displayName = 'Input';

export { Input };
